import { isValidUrl } from '@makyoapp/makyo-common'
import { CornerRadius, HoverEffectVariant, RawSpacingInput } from '@makyoapp/makyo-tw-ui'

import { CoreComponentAction, CoreComponentInfos } from '@/core/models/app-components/types'
import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { useParseVariableSelect } from '@/hooks/__project__/useParseVariableSelect'
import { ComponentSettingKey } from '@/shared/builder'
import { getSafely } from '@/shared/safely'
import { validateImageUrl } from '@/shared/utils'
import { CompOverlayDevmode } from '@/components/__project__/shared/CompOverlayDevmode'

import { convertRatioFormat } from '../../utils'
import { CompVideoProps } from './comp'

export interface CompVideoWrapperProps {
  infos: CoreComponentInfos
  action: CoreComponentAction
  children: (props: CompVideoProps) => React.ReactNode
}

export function CompVideoWrapperCore({
  infos: { component, settings, containerClient, detailTableId, detailRowIndex, currentParentBg },
  action,
  children,
}: CompVideoWrapperProps) {
  const { isDevelopmentMode, openComponentSettings } = useAppBuilderContext()

  // ========================= data =========================
  const src = useParseVariableSelect<string>(
    settings[ComponentSettingKey.VideoDataSrc],
    containerClient,
    detailTableId,
    detailRowIndex
  )

  const cover = useParseVariableSelect<string>(
    settings[ComponentSettingKey.VideoDataCover],
    containerClient,
    detailTableId,
    detailRowIndex
  )

  // ========================= design =========================
  // base
  const width = getSafely(settings, [ComponentSettingKey.VideoDesignWidth], ['100%'])[0]
  const alignment = settings[ComponentSettingKey.VideoDesignHAlign] ?? 'parent'
  const margin = settings?.[ComponentSettingKey.VideoDesignMargin] as RawSpacingInput
  // const padding = parseSpacing(settings?.[ComponentSettingKey.VideoDesignPadding] as SpacingInputValue)

  // video
  // const colorVariant = settings?.[ComponentSettingKey.VideoDesignColorVariant]
  // const progressTop = settings?.[ComponentSettingKey.VideoDesignProgress] !== 'progress-in'
  const hideVolumeControl = !Boolean(settings?.[ComponentSettingKey.VideoDesignVolume])
  const hideControls = !Boolean(settings?.[ComponentSettingKey.VideoDesignControls])
  const cornerRadius = settings?.[ComponentSettingKey.VideoDesignShape] as CornerRadius
  const aspectRatio = settings?.[ComponentSettingKey.VideoDesignRatio]

  const hoverEffect = settings?.[ComponentSettingKey.VideoDesignHoverEffect] as HoverEffectVariant

  // ========================= derived =========================
  const isEmbed = ['youtu.be/', 'youtube.com/'].some((u) => {
    return (src as string)?.replace(/http(s)?:\/\/(www\.)?/g, '').startsWith(u)
  })

  let srcUrl = isEmbed ? (src?.includes('youtube.com/shorts') ? src?.replace(/shorts\//g, 'watch?v=') : src) : src

  if (srcUrl?.includes('?feature=share')) {
    srcUrl = srcUrl.replace('?feature=share', '')
    srcUrl = srcUrl.replace('youtube.com/watch?v=', 'youtu.be/')
  }

  if (srcUrl?.includes('www.youtube.com/watch?v=')) {
    srcUrl = srcUrl.replace('www.youtube.com/watch?v=', 'youtu.be/')
  }

  const shouldShow = !!srcUrl && isValidUrl(srcUrl)

  if (!shouldShow) return null

  const handleClick = () => {
    openComponentSettings(component)
    if (isDevelopmentMode) return
  }

  // ========================= render =========================

  const componentProps: CompVideoProps = {
    // base
    id: component._id,
    width,
    alignment,
    margin,
    backgroundColor: currentParentBg,

    // video
    videoUrl: srcUrl,
    posterUrl: validateImageUrl(cover ?? ''),
    aspectRatio: convertRatioFormat(aspectRatio),
    cornerRadius,
    hideControls,
    hideVolumeControl,
    hoverEffect,
    hoverIntensity: 'md',
  }

  if (isDevelopmentMode && isEmbed) {
    return (
      <div className='relative flex flex-col w-full'>
        {children(componentProps)}
        <CompOverlayDevmode handleClick={handleClick} />
      </div>
    )
  }

  return children(componentProps)
}
